import Avatar from 'flarum/common/components/Avatar';
import username from 'flarum/common/helpers/username';
import ComposerPostPreview from 'flarum/forum/components/ComposerPostPreview';
import { tr } from './translate';
import { showError } from './helpers';

// Resolved on first use. We drive Flarum's real docked composer (app.composer)
// instead of embedding TextEditor inline -- that's the environment FoF Rich
// Text, FoF Upload, Mentions and Emoji are built for, so they all behave
// exactly as in a normal forum reply. Null on stripped-down installs, where
// callers fall back to a plain-textarea editor.
let WikiComposerClass = null;

/**
 * Build a ComposerBody subclass for the docked composer. The caller drives
 * behaviour through attrs so one class serves articles, edits and comments:
 *   onWikiSubmit(content, body)  perform the save; call body.composer.hide()
 *                                  on success.
 *   wikiHeaderItems(body)        optional [{name, content, priority}] rows
 *                                  rendered above the editor.
 */
function makeWikiComposer(ComposerBody) {
  return class WikiComposer extends ComposerBody {
    headerItems() {
      const items = super.headerItems();
      const defs = typeof this.attrs.wikiHeaderItems === 'function' ? this.attrs.wikiHeaderItems(this) : null;
      if (defs && defs.length) {
        defs.forEach((d, i) => {
          if (d == null) return;
          items.add(d.name || 'wiki-header-' + i, d.content, d.priority || 0);
        });
      }
      return items;
    }
    onsubmit() {
      const content = this.composer.fields.content();
      if (typeof this.attrs.onWikiSubmit === 'function') {
        this.attrs.onWikiSubmit(content, this);
      }
    }
  };
}

// Core ships ComposerBody in a lazily-loaded chunk (registered via
// flarum.reg.addChunkModule, not eagerly), so a static import would resolve to
// null at module-init. We resolve it asynchronously: build the WikiComposer
// subclass the first time it's needed (loading the chunk if necessary) and
// cache it. A static `import ComposerBody` would force webpack to eagerly
// require the chunk and break.
const COMPOSER_BODY_PATH = 'flarum/forum/components/ComposerBody';
function ensureWikiComposer() {
  if (WikiComposerClass) return Promise.resolve(WikiComposerClass);
  try {
    const loaded = flarum.reg.checkModule && flarum.reg.checkModule('core', 'forum/components/ComposerBody');
    if (loaded) {
      const base = loaded.default || loaded;
      WikiComposerClass = makeWikiComposer(base);
      return Promise.resolve(WikiComposerClass);
    }
  } catch (e) {}
  try {
    if (flarum.reg.asyncModuleImport) {
      return flarum.reg.asyncModuleImport(COMPOSER_BODY_PATH).then(mod => {
        const base = mod && mod.default || mod || null;
        WikiComposerClass = base ? makeWikiComposer(base) : null;
        return WikiComposerClass;
      }).catch(e => {
        console.error('[linkrobins/wiki] could not load composer chunk:', e);
        return null;
      });
    }
  } catch (e) {}
  return Promise.resolve(null);
}

/**
 * Whether the real docked composer can be used. True when app.composer exists
 * and core's ComposerBody is either loaded or registered in a chunk we can load
 * on demand.
 */
export function wikiComposerAvailable() {
  if (!app.composer || typeof app.composer.load !== 'function') return false;
  if (WikiComposerClass) return true;
  try {
    if (flarum.reg.checkModule && flarum.reg.checkModule('core', 'forum/components/ComposerBody')) return true;
    if (flarum.reg.chunkModules && typeof flarum.reg.chunkModules.has === 'function' && flarum.reg.chunkModules.has('core:forum/components/ComposerBody')) return true;
  } catch (e) {}
  return false;
}

/**
 * Open the docked composer with a WikiComposer body. Loads the composer chunk
 * first if needed. Returns true when the real composer is available; false on
 * stripped installs so callers can fall back to an inline editor.
 */
export function openWikiComposer(attrs) {
  if (!wikiComposerAvailable()) return false;
  if (!attrs.user) attrs.user = app.session && app.session.user;
  ensureWikiComposer().then(Cls => {
    if (!Cls) {
      showError(tr('errors.unknown', 'Unknown error.'));
      return;
    }
    app.composer.load(Cls, attrs).then(() => {
      app.composer.show();
    });
  });
  return true;
}

/**
 * True when the docked composer is currently open with our body for the given
 * marker (set via attrs.wikiContext).
 */
export function wikiComposerOpenFor(contextKey) {
  try {
    if (!app.composer || !app.composer.isVisible || !app.composer.isVisible()) return false;
    const body = app.composer.body;
    const bodyAttrs = body && body.attrs;
    return !!(bodyAttrs && bodyAttrs.wikiContext === contextKey);
  } catch (e) {
    return false;
  }
}

/**
 * Render the same "reply placeholder" Flarum shows at the end of a discussion:
 * a click-to-compose box, or -- while the composer is open for this context --
 * a live preview of what's being typed (ComposerPostPreview).
 */
export function wikiComposerPreview(opts) {
  const user = app.session && app.session.user;
  if (opts.composing) {
    return m('article', {
      className: 'Post CommentPost editing',
      'aria-busy': 'true'
    }, m('div', {
      className: 'Post-container'
    }, [m('div', {
      className: 'Post-side'
    }, user ? m(Avatar, {
      user,
      className: 'Post-avatar'
    }) : null), m('div', {
      className: 'Post-main'
    }, [m('header', {
      className: 'Post-header'
    }, m('div', {
      className: 'PostUser'
    }, m('h3', {
      className: 'PostUser-name'
    }, username(user)))), m('div', {
      className: 'Post-body'
    }, m(ComposerPostPreview, {
      className: 'Post-body',
      composer: app.composer
    }))])]));
  }
  return m('button', {
    type: 'button',
    className: 'Post ReplyPlaceholder',
    onclick: opts.onclick
  }, m('div', {
    className: 'Post-container'
  }, [m('div', {
    className: 'Post-side'
  }, user ? m(Avatar, {
    user,
    className: 'Post-avatar'
  }) : null), m('div', {
    className: 'Post-main'
  }, m('span', {
    className: 'Post-header'
  }, opts.placeholder))]));
}
flarum.reg.add('linkrobins-wiki', 'forum/utils/composer', { wikiComposerAvailable: wikiComposerAvailable,openWikiComposer: openWikiComposer,wikiComposerOpenFor: wikiComposerOpenFor,wikiComposerPreview: wikiComposerPreview, });